import { AutomationSubModule, PrismModule } from '../enums/prism-module.enum';
import { User } from './user.model';

/** Storage objects are immutable; each revision owns a separate copy of their manifest. */
export const ENGINEERING_PROJECT_FILES_BUCKET = 'engineering-project-files';
export const ENGINEERING_PROJECT_FILE_MAX_BYTES = 100_000_000;
export function canViewEngineeringProjectFiles(user: Pick<User, 'active' | 'abilities'> | null | undefined): boolean {
    const automation = user?.abilities?.[PrismModule.Automation];
    return user?.active === true && automation?.visible === true && automation[AutomationSubModule.EngineeringProjectFiles]?.visible === true;
}
export function canEditEngineeringProjectFiles(user: Pick<User, 'active' | 'abilities'> | null | undefined): boolean {
    return canViewEngineeringProjectFiles(user) && user!.abilities[PrismModule.Automation][AutomationSubModule.EngineeringProjectFiles]?.CanEdit === true;
}
export type EngineeringFileRevisionStatus = 'Draft' | 'Released' | 'Obsolete';
export interface EngineeringProjectFile { id: string; path: string; size: number; mimeType: string; }
export interface EngineeringProjectFileData {
    name: string;
    description: string;
    files: EngineeringProjectFile[];
    /** Explicit paths preserve empty folders; older revisions derive them from file paths. */
    folders?: string[];
    primaryMockupFileId: string | null;
    assetIds: string[];
    resourceIds: string[];
    schematicVersionIds: string[];
}
export interface EngineeringFileProject {
    id: string;
    name: string;
    latestRevisionId: string;
    latestRevisionNumber: number;
    draftRevisionId: string | null;
    releasedRevisionId: string | null;
    generation: number;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    lastCommandId: string;
    lastCommandHash: string;
    lastCommandById: string;
}
export interface EngineeringFileRevision {
    id: string;
    projectId: string;
    revisionNumber: number;
    sourceRevisionId: string | null;
    status: EngineeringFileRevisionStatus;
    payloadJson: string;
    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
    releasedOn: Date | null;
    releasedById: string | null;
    releaseComment: string;
}
export interface EngineeringProjectFilesCommand {
    requestId: string;
    type: 'create' | 'save' | 'revise' | 'release' | 'obsolete' | 'deleteDraft';
    projectId: string;
    revisionId: string;
    expectedGeneration: number;
    newRevisionId?: string;
    data?: EngineeringProjectFileData;
    releaseComment?: string;
}
export interface EngineeringProjectFilesResult { project: EngineeringFileProject | null; revision: EngineeringFileRevision | null; }
export function emptyEngineeringProjectFileData(): EngineeringProjectFileData {
    return { name: '', description: '', files: [], folders: [], primaryMockupFileId: null, assetIds: [], resourceIds: [], schematicVersionIds: [] };
}
export function engineeringProjectFileData(revision: EngineeringFileRevision): EngineeringProjectFileData { return JSON.parse(revision.payloadJson); }
export function isEngineeringHtmlFile(file: EngineeringProjectFile): boolean { return /\.html?$/i.test(file.path); }
export function validateEngineeringProjectFileData(data: EngineeringProjectFileData): string | null {
    const id = (value: unknown) => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._-]{0,35}$/.test(value);
    if (!data || typeof data.name !== 'string' || !data.name.trim() || data.name.length > 250) return 'Enter a project name (250 characters or fewer).';
    if (typeof data.description !== 'string' || data.description.length > 20000) return 'Project notes must be 20,000 characters or fewer.';
    if (!Array.isArray(data.files) || data.files.length > 500) return 'A revision can contain up to 500 files.';
    const paths = new Set<string>(), ids = new Set<string>();
    for (const file of data.files) {
        if (!file || !id(file.id) || typeof file.path !== 'string' || file.path.length > 500 || !file.path.trim()
            || /[\\\x00-\x1f]/.test(file.path) || file.path.split('/').some(part => !part.trim() || part === '.' || part === '..')
            || !Number.isSafeInteger(file.size) || file.size < 0 || file.size > ENGINEERING_PROJECT_FILE_MAX_BYTES
            || typeof file.mimeType !== 'string' || file.mimeType.length > 200) return 'A file has an invalid path, type, or size (maximum 100 MB).';
        const path = file.path.toLowerCase();
        if (paths.has(path) || ids.has(file.id)) return 'Each file needs a unique path and storage ID.';
        paths.add(path); ids.add(file.id);
    }
    if (data.folders !== undefined && (!Array.isArray(data.folders) || data.folders.length > 1000
        || data.folders.some(path => typeof path !== 'string' || !path.trim() || path.length > 500 || /[\\\x00-\x1f]/.test(path)
            || path.split('/').some(part => !part.trim() || part === '.' || part === '..'))
        || new Set(data.folders.map(path => path.toLowerCase())).size !== data.folders.length)) return 'Folder paths must be unique, relative paths (up to 1,000 folders).';
    for (const path of [...data.files.map(file => file.path), ...(data.folders ?? [])]) {
        const parts = path.toLowerCase().split('/');
        if (data.folders?.some(folder => folder.toLowerCase() === path.toLowerCase()) && paths.has(path.toLowerCase())) return 'A file and folder cannot have the same path.';
        for (let i = 1; i < parts.length; i++) if (paths.has(parts.slice(0, i).join('/'))) return 'A file cannot contain other files or folders.';
    }
    if (data.primaryMockupFileId !== null && !data.files.some(file => file.id === data.primaryMockupFileId && isEngineeringHtmlFile(file))) return 'Choose an HTML file in this revision as the primary mockup.';
    for (const links of [data.assetIds, data.resourceIds, data.schematicVersionIds]) {
        if (!Array.isArray(links) || links.length > 100 || links.some(value => !id(value)) || new Set(links).size !== links.length) return 'Project links must be unique and limited to 100 per type.';
    }
    if (JSON.stringify(data).length > 900000) return 'The revision manifest is too large.';
    return null;
}
